import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useSocket } from './SocketContext';
import { toast } from 'react-toastify';

const NotificationsContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationsContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationsProvider');
    }
    return context;
};

export const NotificationsProvider = ({ children }) => {
    const [unReadCount, setUnReadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const { socket } = useSocket();

    // Optimized fetch notifications count
    const fetchNotificationsCount = async () => {
        // ✅ Only fetch if user session exists and not on auth/landing page
        const savedUserStr = localStorage.getItem('ff_arena_user_session');
        const isAuthPage = window.location.pathname.startsWith('/auth') || window.location.pathname === '/landing';
        if (!savedUserStr || isAuthPage) {
            setUnReadCount(0);
            return;
        }

        try {
            setLoading(true);
            const response = await axios.get(`${backendUrl}/api/notifications/unread-count`);

            if (response.data.success) {
                setUnReadCount(response.data.count);
            }
        } catch (error) {
            // Silently handle error for count
        } finally {
            setLoading(false);
        }
    };

    // Mark all as read
    const markAllAsRead = async () => {
        try {
            await axios.patch(`${backendUrl}/api/mark-all-read`);

            // Refresh the count after marking as read
            await fetchNotificationsCount();

        } catch (error) {

        }
    };

    // In NotificationsContext.js - add this function
    const markSingleAsRead = async (notificationId) => {
        try {
            await axios.patch(`${backendUrl}/api/mark-read/${notificationId}/read`);

            // Refresh the count after marking as read
            await fetchNotificationsCount();

        } catch (error) {

        }
    };

    // Fetch count on mount
    useEffect(() => {
        fetchNotificationsCount();
    }, []);

    // Real-time notification count updates
    useEffect(() => {
        if (!socket) return;

        let isMounted = true;
        const handleNewNotification = (data) => {
            setUnReadCount(prev => prev + 1);
        };

        const handleBroadcastNotification = (data) => {
            setUnReadCount(prev => prev + 1);
        };

        // ✅ Read from local session instead of redundant HTTP /api/profile call
        const setupListeners = () => {
            const savedUserStr = localStorage.getItem('ff_arena_user_session');
            if (!savedUserStr || !isMounted) return;

            try {
                const user = JSON.parse(savedUserStr);
                const userId = user?.id || user?._id;
                if (!userId) return;

                socket.on('new-notification', handleNewNotification);
                socket.on('broadcast-notification', handleBroadcastNotification);
            } catch (error) {
                // Ignore parse errors
            }
        };

        setupListeners();

        return () => {
            isMounted = false;
            socket.off('new-notification', handleNewNotification);
            socket.off('broadcast-notification', handleBroadcastNotification);
        };
    }, [socket]);

    const value = {
        unReadCount,
        loading,
        fetchNotificationsCount,
        markAllAsRead,
        markSingleAsRead
    };

    return (
        <NotificationsContext.Provider value={value}>
            {children}
        </NotificationsContext.Provider>
    );
};
